import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { GlowCard } from '../components/ui/glow-card';
import { openExternal } from '../utils/telegram';
import { PremiumButton } from '../components/ui/PremiumButton';
import { YCLIENTS_BOOKING_URL } from '../constants';
import { type TranslationKey, useI18n } from '../i18n';

interface PromotionsPageProps { onBack?: () => void; }
interface Promo { id: 'first' | 'father' | 'student'; price: string; imageKey: string; }

const promoImages = import.meta.glob<{ default: string }>('../assets/promos/*.{png,jpg,jpeg,webp,svg,PNG,JPG,JPEG}', { eager: true });
const imageMap: Record<string, string> = {};
for (const path in promoImages) {
  const filename = path.split('/').pop()?.split('.')[0];
  if (filename) imageMap[filename] = promoImages[path].default;
}

const promotions: Promo[] = [
  { id: 'first', price: '200 ₽', imageKey: 'promo_first_haircut' },
  { id: 'father', price: '3 200 ₽', imageKey: 'promo_father_son' },
  { id: 'student', price: '1 600 ₽', imageKey: 'promo_student' },
];

const promoKeys: Record<Promo['id'], { title: TranslationKey; description: TranslationKey; conditions: TranslationKey }> = {
  first: { title: 'promoFirstTitle', description: 'promoFirstDescription', conditions: 'promoFirstConditions' },
  father: { title: 'promoFatherTitle', description: 'promoFatherDescription', conditions: 'promoFatherConditions' },
  student: { title: 'promoStudentTitle', description: 'promoStudentDescription', conditions: 'promoStudentConditions' },
};

const PromoCard: React.FC<{ promo: Promo; index: number }> = ({ promo, index }) => {
  const { t } = useI18n();
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const copy = promoKeys[promo.id];

  return <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 + index * 0.1 }}>
    <GlowCard glowColor="gold" customSize>
      <div className="flex flex-col h-full">
        <div className="relative w-full rounded-xl overflow-hidden bg-gradient-to-br from-sheriff-bg-elevated to-sheriff-bg mb-4 border border-sheriff-border/50" style={{ aspectRatio: '16/10' }}>
          {!imageLoaded && !imageError && <div className="absolute inset-0 flex items-center justify-center"><div className="w-12 h-12 border-4 border-sheriff-accent/20 border-t-sheriff-accent rounded-full animate-spin" /></div>}
          {imageMap[promo.imageKey] && !imageError ? <img src={imageMap[promo.imageKey]} alt={t(copy.title)} loading="lazy" decoding="async" className={`w-full h-full object-contain transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`} onLoad={() => setImageLoaded(true)} onError={() => setImageError(true)} /> : <div className="w-full h-full flex items-center justify-center text-center"><div><div className="text-sm font-medium text-white/40 tracking-wide">HEADMAN</div><div className="text-xs text-white/25 mt-1">{t('imageUnavailable')}</div></div></div>}
        </div>
        <div className="flex-1 flex flex-col px-1">
          <h3 className="text-lg font-medium text-white mb-1.5 leading-tight line-clamp-1 tracking-tight">{t(copy.title)}</h3>
          <p className="text-sm text-white/60 mb-3 leading-relaxed line-clamp-3">{t(copy.description)}</p>
          <div className="mb-3"><span className="text-2xl font-semibold text-white tracking-tight">{promo.price}</span></div>
          <p className="text-xs text-white/45 mb-3 leading-relaxed">{t(copy.conditions)}</p>
          <PremiumButton fullWidth variant="primary" onClick={() => openExternal(YCLIENTS_BOOKING_URL)} className="mt-2">{t('book')}</PremiumButton>
        </div>
      </div>
    </GlowCard>
  </motion.div>;
};

export const PromotionsPage: React.FC<PromotionsPageProps> = ({ onBack }) => {
  const { t } = useI18n();
  return <div className="space-y-6 pb-8">
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center space-y-2">
      <h1 className="text-3xl font-medium text-white tracking-tight">{t('promotionsTitle')}</h1>
      <p className="text-sm text-sheriff-text-secondary mt-2">{t('promotionsSubtitle')}</p>
    </motion.div>
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="space-y-4">
      {promotions.map((promo, index) => <PromoCard key={promo.id} promo={promo} index={index} />)}
    </motion.div>
    {onBack && <PremiumButton fullWidth variant="secondary" onClick={onBack}>← {t('back')}</PremiumButton>}
  </div>;
};
